import { notifyMockDbHotReload } from "@/mock-api/db.events.ts";
import * as _handlers from "@/mock-api/handlers.ts";
import { isVitest } from "@/utils/environment.ts";
import { joinPath } from "@warrenio/utils/joinPath";
import type { RequestHandler, SharedOptions } from "msw";

// Mutable alias, will be overwritten on hot reload
let handlers = _handlers;

/** Base class for starting Mock Service worker. Concrete implementations for browser and Node.js (vitest). */
abstract class MswRegister {
    abstract isRunning(): boolean;
    abstract start(): Promise<void>;
    abstract stop(): void;
    // For hot reload
    abstract resetHandlers(handlers: RequestHandler[]): void;

    async register(enabled: boolean): Promise<void> {
        if (!enabled) {
            if (this.isRunning()) {
                console.info("Stopping mock service worker");
                this.stop();
            }
            return;
        }
        if (this.isRunning()) {
            console.info("Mock service worker already running");
            return;
        }
        await this.start();
    }
}

class BrowserMsw extends MswRegister {
    private worker: import("msw/browser").SetupWorker | undefined;

    isRunning() {
        return !!this.worker;
    }

    async start() {
        const url = joinPath(import.meta.env.BASE_URL, "mockServiceWorker.js");
        console.info("Registering mock service worker: %s", url);
        const { setupWorker } = await import("msw/browser");
        this.worker = setupWorker(...handlers.handlers);
        this.worker.events.on("unhandledException", (error) => {
            console.error("Unhandled exception in MSW", error);
        });
        await this.worker.start({
            serviceWorker: {
                options: { scope: "/" },
                url,
            },
            // MSW dumps every request, response and handler to the console, which spams vitest output.
            quiet: isVitest,
            onUnhandledRequest,
        });
    }

    stop() {
        this.worker!.stop();
        this.worker = undefined;
    }

    resetHandlers(handlers: RequestHandler[]) {
        this.worker?.resetHandlers(...handlers);
    }
}

class NodeJsMsw extends MswRegister {
    private server: import("msw/node").SetupServer | undefined;

    isRunning() {
        return !!this.server;
    }

    async start() {
        const { setupServer } = await import("msw/node");
        console.info("Registering mock service worker for Node.js");
        this.server = setupServer(...handlers.handlers);
        this.server.listen({
            onUnhandledRequest,
        });
        this.server.events.on("request:start", (args) => {
            console.log(requestInfo(args, "🔄 Begin"));
        });
        this.server.events.on("request:match", (args) => {
            console.log(requestInfo(args, "✅ End  "));
        });
        this.server.events.on("request:unhandled", (args) => {
            console.log(requestInfo(args, "❌ Unhandled"));
        });
    }

    stop() {
        this.server!.close();
        this.server = undefined;
    }

    resetHandlers(handlers: RequestHandler[]) {
        this.server?.resetHandlers(...handlers);
    }
}

const graphqlPrefix = "/graphql/";

function matchesApiPrefix(url: string) {
    if (!(url.includes(handlers.apiPrefix) || url.includes(graphqlPrefix))) {
        return false;
    }

    const p = new URL(url);
    if (!(p.pathname.startsWith(handlers.apiPrefix) || p.pathname.startsWith(graphqlPrefix))) {
        return false;
    }

    return true;
}

const onUnhandledRequest: SharedOptions["onUnhandledRequest"] = (request, print) => {
    if (!matchesApiPrefix(request.url)) {
        // console.debug("Ignoring request to", request.url);
        return;
    }
    // Fail the request (safer than bypassing it)
    print.error();
};

function requestInfo({ request, requestId }: { request: Request; requestId: string }, logPrefix: string) {
    const url = new URL(request.url);
    return `[MSW] ${logPrefix}: ${request.method} ${url.pathname}${url.search} (id: ${requestId})`;
}

const instance: MswRegister =
    typeof process !== "undefined" && process.versions?.node ? new NodeJsMsw() : new BrowserMsw();

/** @internal */
export function registerMsw(enabled: boolean) {
    return instance.register(enabled);
}

// Support hot reloading the handlers when they change.
import.meta.hot?.accept(["../handlers.ts"], ([newHandlers]: unknown[]) => {
    handlers = newHandlers as typeof _handlers;
    instance.resetHandlers(handlers.handlers);

    notifyMockDbHotReload();
});
